import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Table,
  Space,
  Skeleton,
  Alert,
  Input,
  Select,
  Tag,
  Typography,
  Grid,
  Segmented,
  Button,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  AppstoreOutlined,
  BarsOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  FilterOutlined,
  ProjectOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { FilterDropdown } from "@refinedev/antd";
import { useDelete } from "@refinedev/core";
import { Link, useLocation, useNavigate } from "react-router-dom";

const { useBreakpoint } = Grid;
import { useTranslation } from "react-i18next";
import type { TableProps } from "antd";
import type { GridColumnMetadata } from "../../types";
import { useItemTypeMetadata } from "../../hooks/useItemTypeMetadata";
import { useColumnRenderers } from "../../hooks/useColumnRenderers";
import { useColumnPreferences } from "../../hooks/useColumnPreferences";
import { useListValues } from "../../hooks/useListValues";
import { useSiteContext } from "../../contexts/SiteContext";
import {
  getColumnsWithOverrides,
  getFilterableItemRefs,
} from "../../config/gridColumns";
import { useRelatedItems } from "../../hooks/useRelatedItems";
import { ColumnSettings } from "../ColumnSettings";
import { snakeToCamel } from "../../utils/fieldMapping";
import { sanitizeForDuplicate } from "../../utils/duplicateUtils";
import { resolveDisplayValue, isGuid } from "../../utils/displayUtils";
import { ENV } from "../../config/env";
import { KanbanBoard } from "../KanbanBoard";
import KanbanVoteModal from "../KanbanBoard/KanbanVoteModal";
import { useKanbanGrouping } from "../../hooks/useKanbanGrouping";
import { useKanbanMutation } from "../../hooks/useKanbanMutation";
import { useLifecycleMap } from "../../hooks/useLifecycleMap";
import { CardView } from "./CardView";
import { OrgChartView } from "../OrgChartView";

const { Text } = Typography;

type RenderFn = (value: unknown, record: Record<string, unknown>) => ReactNode;
type ViewMode = "table" | "card" | "kanban" | "orgchart";

// Minimum column width when resizing
const MIN_COLUMN_WIDTH = 60;

/**
 * Resizable header cell component — adds a drag handle at the right edge
 * of table header cells, similar to the sidebar resize pattern.
 */
interface ResizableHeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  width?: number;
  onResize?: (width: number) => void;
}

function ResizableHeaderCell({
  width,
  onResize,
  children,
  style,
  ...restProps
}: ResizableHeaderCellProps) {
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const currentWidth = useRef(width || 0);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const rafRef = useRef<number>(0);

  // If no width or no onResize callback, render a plain <th>
  if (!width || !onResize) {
    return (
      <th {...restProps} style={style}>
        {children}
      </th>
    );
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    currentWidth.current = width;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging.current) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const delta = moveEvent.clientX - startX.current;
        const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth.current + delta);
        currentWidth.current = newWidth;
        // Live preview: update cell width directly for smooth feedback
        if (cellRef.current) {
          cellRef.current.style.width = `${newWidth}px`;
          cellRef.current.style.minWidth = `${newWidth}px`;
          cellRef.current.style.maxWidth = `${newWidth}px`;
        }
      });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      cancelAnimationFrame(rafRef.current);
      // Persist the final width
      if (currentWidth.current !== startWidth.current) {
        onResize(currentWidth.current);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <th {...restProps} ref={cellRef} style={{ ...style, position: "relative" }}>
      {children}
      {/* Drag handle */}
      <span
        className="column-resize-handle"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          right: -2,
          top: 0,
          bottom: 0,
          width: 5,
          cursor: "col-resize",
          zIndex: 1,
        }}
      />
    </th>
  );
}

const KANBAN_PAGE_SIZE = 100;
const DEFAULT_PAGE_SIZE = 10;

// Check if row click navigation is enabled via env variable
const isRowClickEnabled = ENV.ENABLE_ROW_CLICK_EDIT;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DynamicTableProps<T = any> {
  resource: string;
  itemTypeName: string;
  tableProps: TableProps<T>;
  excludeColumns?: string[];
  includeActions?: boolean;
  customRenderers?: Record<string, RenderFn>;
  showColumnSettings?: boolean;
  /** Content rendered on the left side of the toolbar (e.g. search input) */
  toolbarLeft?: ReactNode;
  /** Enable row click to navigate to edit page. Defaults to env VITE_ENABLE_ROW_CLICK_EDIT */
  enableRowClick?: boolean;
  /** Callback to change page size (for kanban mode) */
  setPageSize?: (size: number) => void;
  /** Callback to reset to page 1 */
  setCurrent?: (page: number) => void;
  /** Total number of items on server (for kanban truncation warning) */
  total?: number;
  /** Refine setFilters callback — needed for visible item-reference filters */
  setFilters?: (
    fn: (
      prev: import("@refinedev/core").CrudFilter[],
    ) => import("@refinedev/core").CrudFilter[],
  ) => void;
  /** Current active filters from Refine */
  filters?: import("@refinedev/core").CrudFilter[];
  /** Enable org chart view mode (for hierarchical item types like Department) */
  enableOrgChart?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DynamicTable<T = any>({
  resource,
  itemTypeName,
  tableProps,
  excludeColumns = [],
  includeActions = true,
  customRenderers = {},
  showColumnSettings = true,
  toolbarLeft,
  enableRowClick = isRowClickEnabled,
  setPageSize,
  setCurrent,
  total,
  setFilters,
  filters,
  enableOrgChart = false,
}: DynamicTableProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const referrerUrl = `${location.pathname}${location.search}`;
  const screens = useBreakpoint();
  const { mutate: deleteItem, isLoading: isDeleteLoading } = useDelete();

  // Responsive values
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  // Track manual view mode override
  const [manualViewMode, setManualViewMode] = useState<ViewMode | null>(null);

  // Compute effective view mode: user choice takes priority, otherwise auto-detect
  const viewMode: ViewMode = manualViewMode ?? (isMobile ? "card" : "table");

  const handleViewModeChange = (mode: ViewMode) => {
    setManualViewMode(mode);
  };

  const {
    gridColumns,
    metadata,
    isLoading: metadataLoading,
    isError,
  } = useItemTypeMetadata({
    itemTypeName,
  });

  // Kanban grouping detection (lifecycle-based)
  const { isKanbanAvailable, lifecycleMapId } = useKanbanGrouping({
    metadata,
  });

  // Fetch lifecycle map for kanban columns and card state tags
  const { lifecycleMap } = useLifecycleMap({
    mapId: lifecycleMapId ?? undefined,
    enabled: isKanbanAvailable || viewMode === "card",
  });

  // Kanban mutation hook (always called, but only used in kanban mode)
  const { moveItem, isMoving, pendingVote, submitVote, clearPendingVote } =
    useKanbanMutation({
      resource,
      itemTypeName,
    });

  // Show kanban option only on non-mobile and when a grouping field exists
  const showKanbanOption = isKanbanAvailable && !isMobile;

  // Handle page size switching when toggling kanban mode
  const prevViewMode = useRef<ViewMode | null>(null);
  useEffect(() => {
    if (prevViewMode.current !== viewMode) {
      if (viewMode === "kanban" && setPageSize && setCurrent) {
        setPageSize(KANBAN_PAGE_SIZE);
        setCurrent(1);
      } else if (
        prevViewMode.current === "kanban" &&
        setPageSize &&
        setCurrent
      ) {
        setPageSize(DEFAULT_PAGE_SIZE);
        setCurrent(1);
      }
      prevViewMode.current = viewMode;
    }
  }, [viewMode, setPageSize, setCurrent]);
  const { getRenderer } = useColumnRenderers();
  const { availableSites, isEnabled: isSiteScopingEnabled } = useSiteContext();

  // Process columns from API metadata (fully dynamic, no fallback)
  const sourceColumns = useMemo(() => {
    if (gridColumns.length === 0) {
      return [];
    }
    const cols = getColumnsWithOverrides(gridColumns, itemTypeName);

    // Move siteId column to prominent position for any item type that has it in metadata
    if (isSiteScopingEnabled) {
      const siteColIndex = cols.findIndex((col) => col.name === "siteId");
      if (siteColIndex > 2) {
        // Remove from original position and re-insert at position 2
        const [siteCol] = cols.splice(siteColIndex, 1);
        const insertIndex = Math.min(2, cols.length);
        cols.splice(insertIndex, 0, {
          ...siteCol,
          label: t("site.title", "Branch"),
          width: 130,
          sortOrder: insertIndex,
        });
      }
    }

    return cols;
  }, [gridColumns, itemTypeName, isSiteScopingEnabled, t]);

  // Column preferences for user customization
  const {
    visibleColumns,
    preferences,
    toggleColumn,
    setColumnWidth,
    resetToDefault,
  } = useColumnPreferences({
    resource,
    defaultColumns: sourceColumns,
  });

  // Filter out excluded columns
  const filteredColumns = useMemo(
    () => visibleColumns.filter((col) => !excludeColumns.includes(col.name)),
    [visibleColumns, excludeColumns],
  );

  // Helper: check if column is a list type (Aras has "list", "filter list", "mv_list")
  const isListColumn = (col: { dataType: string; dataSource?: string }) =>
    (col.dataType === "list" ||
      col.dataType === "filter list" ||
      col.dataType === "mv_list") &&
    col.dataSource;

  // Extract list names from columns that have dataSource
  const listNames = useMemo(() => {
    const names = sourceColumns
      .filter(isListColumn)
      .map((col) => col.dataSource as string);
    return [...new Set(names)]; // Remove duplicates
  }, [sourceColumns]);

  // Fetch list values for all list columns
  const { lists } = useListValues({
    listNames,
    enabled: listNames.length > 0,
  });

  // Identify all item-reference columns with a dataSource (for column header filters)
  const allItemRefConfigs = useMemo(
    () =>
      sourceColumns
        .filter((col) => col.dataType === "item" && col.dataSource)
        .map((col) => ({ itemType: col.dataSource!, columnName: col.name })),
    [sourceColumns],
  );

  // Subset: columns configured for visible filter bars above the table
  const filterableItemRefNames = useMemo(
    () => getFilterableItemRefs(itemTypeName),
    [itemTypeName],
  );
  const itemRefFilterConfigs = useMemo(
    () =>
      allItemRefConfigs.filter((c) =>
        filterableItemRefNames.includes(c.columnName),
      ),
    [allItemRefConfigs, filterableItemRefNames],
  );

  // Fetch related items for ALL item-reference columns (used by both column header & visible filters)
  const { items: itemRefFilterOptions } = useRelatedItems({
    relatedItems: allItemRefConfigs.map((c) => ({ itemType: c.itemType })),
    enabled: allItemRefConfigs.length > 0,
  });

  // Create a lookup map: { columnName: { value: label } }
  const listLabelLookup = useMemo(() => {
    const lookup: Record<string, Record<string, string>> = {};

    sourceColumns.filter(isListColumn).forEach((col) => {
      const listName = col.dataSource as string;
      const listValues = lists[listName] || [];

      lookup[col.name] = {};
      listValues.forEach((lv) => {
        lookup[col.name][lv.value] = lv.label || lv.value;
      });
    });

    return lookup;
  }, [sourceColumns, lists]);

  // Create custom renderers for list columns that display labels
  const listRenderers = useMemo(() => {
    const renderers: Record<string, RenderFn> = {};

    sourceColumns.filter(isListColumn).forEach((col) => {
      renderers[col.name] = (value: unknown) => {
        if (!value) return <Text type="secondary">-</Text>;

        const valueStr = String(value);
        const label = listLabelLookup[col.name]?.[valueStr] || valueStr;

        return (
          <Tooltip title={label} placement="topLeft">
            <Tag>{label}</Tag>
          </Tooltip>
        );
      };
    });

    return renderers;
  }, [sourceColumns, listLabelLookup]);

  // Create custom renderers for site column (show site description instead of ID)
  const siteRenderers = useMemo(() => {
    const renderers: Record<string, RenderFn> = {};
    if (!isSiteScopingEnabled) return renderers;

    const siteRenderer: RenderFn = (
      value: unknown,
      record: Record<string, unknown>,
    ) => {
      // Prefer backend-resolved siteIdDisplay, fall back to client-side lookup
      const siteLabel = (record.siteIdDisplay ?? record.siteLabel) as
        | string
        | undefined;
      if (siteLabel)
        return (
          <Tooltip title={siteLabel} placement="topLeft">
            <Tag>{siteLabel}</Tag>
          </Tooltip>
        );

      if (!value) return <Text type="secondary">-</Text>;
      const site = availableSites.find((s) => s.siteId === String(value));
      const displayText = site?.description || site?.siteName || String(value);
      return (
        <Tooltip title={displayText} placement="topLeft">
          <Tag>{displayText}</Tag>
        </Tooltip>
      );
    };

    // Support both snake_case (from Aras metadata) and camelCase (injected) column names
    if (sourceColumns.some((col) => col.name === "site_id")) {
      renderers["site_id"] = siteRenderer;
    }
    if (sourceColumns.some((col) => col.name === "siteId")) {
      renderers["siteId"] = siteRenderer;
    }
    return renderers;
  }, [sourceColumns, availableSites, isSiteScopingEnabled]);

  // Create custom renderers for item reference columns (use Display field if available)
  // Link to the referenced item's detail page when the raw value is a valid GUID
  const itemRefRenderers = useMemo(() => {
    const renderers: Record<string, RenderFn> = {};

    sourceColumns
      .filter((col) => col.dataType === "item")
      .forEach((col) => {
        const displayFieldName = snakeToCamel(col.name) + "Display";
        const refResource = col.dataSource; // e.g. "EducationFacility"
        renderers[col.name] = (
          value: unknown,
          record: Record<string, unknown>,
        ) => {
          // Use resolveDisplayValue to prefer Display field and filter out GUIDs
          const resolved = resolveDisplayValue(value, record[displayFieldName]);
          if (resolved === "-") return <Text type="secondary">-</Text>;

          // Link to the referenced item if we have a resource name and a valid GUID
          const itemId =
            typeof value === "string" && isGuid(value) ? value : null;
          if (refResource && itemId) {
            return (
              <Tooltip title={resolved} placement="topLeft">
                <Link
                  to={`/${refResource}/${itemId}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {resolved}
                </Link>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={resolved} placement="topLeft">
              <Text>{resolved}</Text>
            </Tooltip>
          );
        };
      });

    return renderers;
  }, [sourceColumns]);

  // Determine which columns have active filters (for header highlight)
  const getFilteredValue = (columnName: string): string[] | null => {
    if (!filters) return null;
    const f = filters.find(
      (f) =>
        "field" in f &&
        f.field === columnName &&
        "value" in f &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== "",
    );
    if (f && "value" in f) return [String(f.value)];
    return null;
  };

  // Fields managed by search bar / quick filter — not column filters
  const systemFilterFields = new Set(["search", "searchField", "state"]);

  // Check if any column-level filters are active
  const hasColumnFilters = useMemo(() => {
    if (!filters) return false;
    return filters.some(
      (f) =>
        "field" in f &&
        !systemFilterFields.has(f.field) &&
        "value" in f &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Build filter dropdown for a column
  const getFilterDropdown = (column: GridColumnMetadata) => {
    // Site column: use available sites as filter options
    if (
      (column.name === "siteId" || column.name === "site_id") &&
      isSiteScopingEnabled &&
      availableSites.length > 0
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (props: any) => (
        <FilterDropdown {...props}>
          <Select
            style={{ width: 200 }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("site.selectSite", "Select site")}
            options={availableSites.map((s) => ({
              label: s.description || s.siteName,
              value: s.siteId,
            }))}
          />
        </FilterDropdown>
      );
    }

    // Item-reference columns store GUIDs — use a Select dropdown with lazy-loaded options
    if (column.dataType === "item" && column.dataSource) {
      const options = itemRefFilterOptions[column.dataSource] || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (props: any) => (
        <FilterDropdown {...props}>
          <Select
            style={{ width: 250 }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("table.selectFilter", "Chọn...")}
            loading={options.length === 0}
            options={options.map((opt) => ({
              label: opt.description
                ? `${opt.label} — ${opt.description}`
                : opt.label,
              value: opt.value,
            }))}
          />
        </FilterDropdown>
      );
    }
    // Item columns without dataSource — no meaningful filter possible
    if (column.dataType === "item") {
      return undefined;
    }

    // Only show Select dropdown if column has dataSource AND list values are available
    if (column.dataSource) {
      const listValues = lists[column.dataSource] || [];
      if (listValues.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (props: any) => (
          <FilterDropdown {...props}>
            <Select
              style={{ width: 200 }}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("table.selectFilter", "Chọn...")}
              options={listValues.map((lv) => ({
                label: lv.label || lv.value,
                value: lv.value,
              }))}
            />
          </FilterDropdown>
        );
      }
      // Fall through to text filter if list values are empty
    }

    // Text filter with LIKE matching (wraps value with % for partial match)
    // Uses Ant Design filter API directly instead of Refine FilterDropdown
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (props: any) => {
      const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props;
      const displayValue = selectedKeys[0]
        ? String(selectedKeys[0]).replace(/^%|%$/g, "")
        : "";

      return (
        <div style={{ padding: 8 }}>
          <Input
            value={displayValue}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [`%${e.target.value}%`] : [])
            }
            onPressEnter={() => confirm()}
            placeholder={t("table.searchName", "Tìm kiếm...")}
            style={{ width: 200, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              {t("buttons.filter", "Filter")}
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              {t("buttons.clear", "Clear")}
            </Button>
          </Space>
        </div>
      );
    };
  };

  // Column width constraints - responsive
  const DEFAULT_WIDTH = 150;
  const MAX_COLUMN_WIDTH = isTablet ? 150 : 200;

  // Merge renderers: customRenderers (highest priority) > siteRenderers > listRenderers > itemRefRenderers > getRenderer (default)
  const mergedRenderers = useMemo(
    () => ({
      ...itemRefRenderers,
      ...listRenderers,
      ...siteRenderers,
      ...customRenderers,
    }),
    [itemRefRenderers, listRenderers, siteRenderers, customRenderers],
  );

  // Callback for column resize — persists the new width
  const handleColumnResize = useCallback(
    (columnName: string, newWidth: number) => {
      setColumnWidth(columnName, Math.round(newWidth));
    },
    [setColumnWidth],
  );

  // Build table columns from metadata
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableColumns: any[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cols: any[] = filteredColumns.map((col) => {
      const headerText = t("table." + col.name, col.label);
      // Estimate minimum width to fit header text: ~14px per char + 40px for padding/sort icon
      const minHeaderWidth = headerText.length * 14 + 40;
      // Use user-set width if available, otherwise ensure header text fits
      const hasUserWidth = col.name in (preferences.columnWidths || {});
      const rawWidth = col.width || DEFAULT_WIDTH;
      const columnWidth = hasUserWidth
        ? rawWidth
        : Math.max(minHeaderWidth, Math.min(rawWidth, MAX_COLUMN_WIDTH));

      return {
        key: col.name,
        dataIndex: snakeToCamel(col.name),
        title: headerText,
        width: columnWidth,
        align: col.alignment,
        sorter: true,
        filterDropdown: getFilterDropdown(col),
        filteredValue: getFilteredValue(col.name),
        render: mergedRenderers[col.name] || getRenderer(col),
        // Apply ellipsis to ALL columns for single-line display (showTitle: false to use custom tooltip in renderer)
        ellipsis: { showTitle: false },
        // Attach resize handler via onHeaderCell
        onHeaderCell: () => ({
          width: columnWidth,
          onResize: (newWidth: number) =>
            handleColumnResize(col.name, newWidth),
        }),
      };
    });

    // Add actions column - narrower on mobile
    if (includeActions) {
      cols.push({
        key: "actions",
        title: t("table.actions"),
        fixed: "right",
        width: isMobile ? 100 : isTablet ? 130 : 180,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (_: unknown, record: any) => (
          <Space
            onClick={(e) => e.stopPropagation()}
            size={isMobile ? 0 : 4}
            style={{
              padding: "2px 4px",
              borderRadius: 6,
              background: "rgba(0, 0, 0, 0.02)",
            }}
          >
            <Tooltip title={t("buttons.show", "View")}>
              <Link
                to={`/${resource}/${record.id}`}
                state={{ from: referrerUrl }}
              >
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  style={{ color: "#1677ff" }}
                />
              </Link>
            </Tooltip>
            <Tooltip title={t("buttons.edit", "Edit")}>
              <Link
                to={`/${resource}/${record.id}/edit`}
                state={{ from: referrerUrl }}
              >
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ color: "#52c41a" }}
                />
              </Link>
            </Tooltip>
            <Tooltip title={t("buttons.duplicate", "Duplicate")}>
              <Link
                to={`/${resource}/create`}
                state={{
                  from: referrerUrl,
                  duplicateFrom: sanitizeForDuplicate(
                    record as Record<string, unknown>,
                  ),
                }}
              >
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  size="small"
                  style={{ color: "#722ed1" }}
                />
              </Link>
            </Tooltip>
            <Popconfirm
              title={t("buttons.confirm", "Are you sure?")}
              description={t(
                "buttons.confirmDelete",
                "This action cannot be undone.",
              )}
              onConfirm={() =>
                deleteItem({
                  resource,
                  id: record.id,
                  successNotification: {
                    message: t(
                      "notifications.deleteSuccess",
                      "Successfully deleted",
                    ),
                    type: "success",
                  },
                  errorNotification: (error) => ({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    message:
                      (error as any)?.message ||
                      t("notifications.deleteError", "Error when deleting"),
                    type: "error",
                  }),
                })
              }
              okText={t("buttons.yes", "Yes")}
              cancelText={t("buttons.no", "No")}
            >
              <Tooltip title={t("buttons.delete", "Delete")}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  loading={isDeleteLoading}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredColumns,
    mergedRenderers,
    getRenderer,
    includeActions,
    isMobile,
    isTablet,
    resource,
    t,
    handleColumnResize,
    preferences.columnWidths,
    itemRefFilterConfigs,
    filters,
  ]);

  // Calculate total table width for horizontal scroll
  const totalTableWidth = useMemo(() => {
    return tableColumns.reduce(
      (sum, col) => sum + (col.width || DEFAULT_WIDTH),
      0,
    );
  }, [tableColumns]);

  // Show loading state
  if (metadataLoading && sourceColumns.length === 0) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  // Show error if metadata failed and no fallback
  if (isError && sourceColumns.length === 0) {
    return (
      <Alert
        message={t("errors.metadataFailed", "Không thể tải cấu hình bảng")}
        description={t(
          "errors.metadataFailedDesc",
          "Vui lòng thử lại hoặc liên hệ hỗ trợ.",
        )}
        type="error"
        showIcon
      />
    );
  }

  // Get data source from tableProps
  const dataSource = tableProps.dataSource || [];
  const isLoading = tableProps.loading;

  return (
    <div>
      {/* Toolbar with optional left content, view toggle and column settings */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: toolbarLeft ? "space-between" : "flex-end",
          alignItems: "center",
          gap: 8,
          overflow: "hidden",
        }}
      >
        {toolbarLeft}
        <Space size={isMobile ? 4 : 8} style={{ flexShrink: 0 }}>
          <Segmented
            value={viewMode}
            onChange={(value) => handleViewModeChange(value as ViewMode)}
            options={[
              { value: "card", icon: <AppstoreOutlined /> },
              { value: "table", icon: <BarsOutlined /> },
              ...(showKanbanOption
                ? [{ value: "kanban" as const, icon: <ProjectOutlined /> }]
                : []),
              ...(enableOrgChart
                ? [{ value: "orgchart" as const, icon: <ApartmentOutlined /> }]
                : []),
            ]}
            size={isMobile ? "small" : "middle"}
          />
          {showColumnSettings && viewMode === "table" && (
            <ColumnSettings
              columns={sourceColumns}
              hiddenColumns={preferences.hiddenColumns}
              onToggleColumn={toggleColumn}
              onResetToDefault={resetToDefault}
            />
          )}
          {hasColumnFilters && setFilters && (
            <Tooltip
              title={t("table.clearAllFilters", "Xóa tất cả bộ lọc cột")}
            >
              <Button
                icon={<FilterOutlined />}
                size={isMobile ? "small" : "middle"}
                danger
                onClick={() => {
                  setFilters((prev) =>
                    prev.filter(
                      (f) => "field" in f && systemFilterFields.has(f.field),
                    ),
                  );
                }}
              >
                {!isMobile && t("table.resetFilters", "Xóa lọc")}
              </Button>
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Visible item-reference filters (e.g., filter by EducationFacility) */}
      {setFilters && itemRefFilterConfigs.length > 0 && (
        <Space wrap size="small" style={{ marginBottom: 12 }}>
          {itemRefFilterConfigs.map((cfg) => {
            const col = sourceColumns.find((c) => c.name === cfg.columnName);
            const options = itemRefFilterOptions[cfg.itemType] || [];
            const activeFilter = filters?.find(
              (f) => "field" in f && f.field === cfg.columnName,
            );
            const activeValue =
              activeFilter && "value" in activeFilter
                ? String(activeFilter.value)
                : undefined;
            return (
              <Select
                key={cfg.columnName}
                style={{ minWidth: 220 }}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={
                  col ? t(`table.${col.name}`, col.label) : cfg.columnName
                }
                value={activeValue}
                onChange={(value) => {
                  setFilters((prev) => {
                    const without = prev.filter(
                      (f) => !("field" in f) || f.field !== cfg.columnName,
                    );
                    if (value) {
                      return [
                        ...without,
                        {
                          field: cfg.columnName,
                          operator: "eq" as const,
                          value,
                        },
                      ];
                    }
                    return without;
                  });
                }}
                options={options.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />
            );
          })}
        </Space>
      )}

      {/* Content: Table, Cards, Kanban, or Org Chart */}
      {viewMode === "orgchart" && enableOrgChart ? (
        <OrgChartView resource={resource} itemTypeName={itemTypeName} />
      ) : viewMode === "kanban" && isKanbanAvailable ? (
        <KanbanBoard
          data={dataSource as Record<string, unknown>[]}
          loading={!!isLoading}
          columns={filteredColumns}
          resource={resource}
          lifecycleMap={lifecycleMap}
          listLabelLookup={listLabelLookup}
          onItemMove={moveItem}
          isMoving={isMoving}
          total={total}
        />
      ) : viewMode === "table" ? (
        <Table
          {...tableProps}
          columns={tableColumns}
          components={{
            header: {
              cell: ResizableHeaderCell,
            },
          }}
          rowKey="id"
          scroll={{ x: isMobile ? "100%" : totalTableWidth }}
          tableLayout="fixed"
          size={isMobile ? "small" : "middle"}
          pagination={{
            ...tableProps.pagination,
            size: isMobile ? "small" : "default",
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            simple: isMobile,
            showTotal: (total) => t("pagination.total", { total }),
          }}
          onRow={
            enableRowClick
              ? (record) => ({
                  onClick: () => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const id = (record as any).id;
                    if (id) {
                      navigate(`/${resource}/${id}/edit`, {
                        state: { from: referrerUrl },
                      });
                    }
                  },
                  style: { cursor: "pointer" },
                })
              : undefined
          }
        />
      ) : (
        <CardView
          dataSource={dataSource as Record<string, unknown>[]}
          columns={filteredColumns}
          resource={resource}
          loading={!!isLoading}
          enableRowClick={enableRowClick}
          includeActions={includeActions}
          mergedRenderers={mergedRenderers}
          getRenderer={getRenderer}
          lifecycleMap={lifecycleMap}
          pagination={tableProps.pagination || undefined}
          isMobile={isMobile}
          onPageChange={(page, pageSize) => {
            setCurrent?.(page);
            setPageSize?.(pageSize);
          }}
        />
      )}

      {/* Kanban vote modal — shown when drag-and-drop detects a pending workflow */}
      {pendingVote && (
        <KanbanVoteModal
          open={!!pendingVote}
          activity={pendingVote.activity}
          loading={isMoving}
          onSubmit={submitVote}
          onCancel={clearPendingVote}
        />
      )}
    </div>
  );
}

export default DynamicTable;
